import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useRef,
} from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase, Profile } from "@/lib/supabase";

interface AuthContextType {
  user: any; // Using any to accommodate both User and Profile properties
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  profile: null,
  loading: true,
  signOut: async () => {},
  refreshProfile: async () => {},
});

/**
 * Robust recovery helper to resolve stale/broken sessions.
 */
function attemptSessionCleanup(reason: string) {
  console.error(`[Auth] Cleanup required: ${reason}`);
  try {
    // Clear Supabase localStorage keys
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.includes('sb-') || key.includes('supabase') || key.includes('ib-meridian') || key.includes('ib_meridian'))) {
            localStorage.removeItem(key);
        }
    }
    // Hard redirect back to auth to ensure a fresh bridge
    if (window.location.pathname !== '/login') {
       window.location.href = '/login';
    }
  } catch (err) {
    console.error("[Auth] Cleanup failure:", err);
  }
}

async function upsertProfileFromSocialMetadata(user: User): Promise<Profile | null> {
  try {
    const { full_name, avatar_url, name, picture } = user.user_metadata || {};
    
    // Facebook/Google sometimes use different keys. 
    // We prioritize full_name and avatar_url as requested.
    const resolvedName = full_name || name || user.email?.split('@')[0] || "Student";
    const resolvedAvatar = avatar_url || picture || "";

    const { data, error } = await supabase
      .from("profiles")
      .upsert({
        id: user.id,
        email: user.email,
        full_name: resolvedName,
        avatar_url: resolvedAvatar,
        role: "student", // Default only for NEW users — existing roles are preserved via ignoreDuplicates
        updated_at: new Date().toISOString(),
      }, { onConflict: 'id', ignoreDuplicates: true }) // ✅ FIXED: never overwrite existing profile role
      .select()
      .single();

    if (error) {
      if (!error.message.includes('409') && !error.message.includes('duplicate')) {
         console.error("[Auth] Metadata sync error:", error);
      }
      return null;
    }
    return data as Profile;
  } catch (err) {
    console.error("[Auth] Metadata sync exception:", err);
    return null;
  }
}

// Last-known profile, used to render instantly on reload while the fresh copy loads.
// Key contains "ib_meridian" so the sign-out/cleanup sweeps remove it.
const PROFILE_CACHE_KEY = "ib_meridian_profile_cache";

function readCachedProfile(userId: string): Profile | null {
  try {
    const cached = JSON.parse(localStorage.getItem(PROFILE_CACHE_KEY) || "null");
    return cached?.id === userId ? (cached as Profile) : null;
  } catch {
    return null;
  }
}

function writeCachedProfile(p: Profile | null) {
  try {
    if (p) localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(p));
    else localStorage.removeItem(PROFILE_CACHE_KEY);
  } catch { /* storage unavailable */ }
}

async function fetchProfileById(userId: string, retries = 3): Promise<Profile | null> {
  try {
    const { data, error } = await supabase
      .from("profiles")
      .select("id, email, full_name, role, credit_hours, avatar_url, bio, subjects, created_at")
      .eq("id", userId)
      .single();

    if (error) {
      if (retries > 0) {
        await new Promise((r) => setTimeout(r, 1000));
        return fetchProfileById(userId, retries - 1);
      }
      return null;
    }
    return data as Profile;
  } catch (err) {
    console.error("[Auth] fetchProfileById exception:", err);
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const signingOut = useRef(false); // Guard: block auth events during sign-out

  // Use refs to avoid stale closures in the onAuthStateChange listener
  const profileRef = useRef<Profile | null>(null);
  const loadingRef = useRef(true);

  const applyProfile = (p: Profile) => {
    setProfile(p);
    profileRef.current = p;
    writeCachedProfile(p);
  };

  const refreshProfile = async () => {
    if (!user) return;
    const p = await fetchProfileById(user.id);
    if (p) applyProfile(p);
  };

  const signOut = async () => {
    if (signingOut.current) return; // Prevent double-trigger
    signingOut.current = true;
    console.log("[Auth] Initiating secure termination...");
    try {
      // 1. Wipe local state immediately so no component re-renders with stale data
      setUser(null);
      setProfile(null);
      setSession(null);

      // 2. Collect ALL Supabase/app keys BEFORE deleting (avoids index-shift bug)
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.includes('sb-') || key.includes('supabase') || key.includes('ib-meridian') || key.includes('ib_meridian'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(k => localStorage.removeItem(k));
      window.sessionStorage.clear();

      // 3. Tell Supabase to revoke the server-side token
      await supabase.auth.signOut({ scope: 'global' });
    } catch (err) {
      console.warn("[Auth] Supabase signOut call failed, local purge already done:", err);
    } finally {
      // 4. Hard redirect — no SPA routing so Supabase cannot intercept
      console.log("[Auth] Session purged. Redirecting to login...");
      window.location.replace('/login');
    }
  };

  useEffect(() => {
    let mounted = true;
    let authSubscription: any = null;

    // Safety hydration exit for slow networks (MISSION: 12s)
    const safetyTimeout = setTimeout(() => {
      if (mounted && loadingRef.current) {
        console.warn("[Auth] Initializing session took too long (12s). Forcing interactive state.");
        setLoading(false);
        loadingRef.current = false;
      }
    }, 12000);

    const setupAuth = async () => {
      try {
        setLoading(true);
        // 1. Recover Session Immediately
        const { data: { session: initialSession }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) throw sessionError;

        if (initialSession && mounted) {
          console.log("[Auth] Session recovered:", true);
          localStorage.setItem('ib_meridian_auth', 'true');
          setSession(initialSession);
          setUser(initialSession.user);

          // 2a. Fast path on reload: render with the cached profile right away and
          //     refresh it in the background instead of blocking the UI on a round trip.
          const cached = readCachedProfile(initialSession.user.id);
          if (cached) {
            setProfile(cached);
            profileRef.current = cached;
            setLoading(false);
            loadingRef.current = false;
            fetchProfileById(initialSession.user.id, 2).then(fresh => {
              if (mounted && fresh) applyProfile(fresh);
            });
          }

          // 2b. No cache (first load on this device): await profile fetch before hydration
          let p = cached ? null : await fetchProfileById(initialSession.user.id, 2);

          if (mounted && !cached) {
            if (!p) {
               // If no profile exists BUT they used Google/Social Auth, this is their first time logging in!
               if (initialSession.user.app_metadata?.provider !== 'email') {
                 console.log("[Auth] First-time social user mapping during initialization...");
                 p = await upsertProfileFromSocialMetadata(initialSession.user);
                 
                 // Trigger Welcome Email & Admin Alert natively!
                 if (p && initialSession.user.email) {
                    const { sendAdminNotification } = await import("@/lib/notifications");
                    try {
                      await sendAdminNotification('student_welcome', {
                        email: initialSession.user.email,
                        name: p.full_name
                      });
                      console.log("[Auth] Initial OAuth Welcome email processed successfully.");
                    } catch (e) { console.error("[Auth] Welcome Mail drop:", e); }
                 }
               } else {
                 console.warn("[Auth] Ghost email user detected (No Profile). Forcing deep cleanup...");
                 signOut();
                 return;
               }
            }
            if (p) {
              applyProfile(p);
              console.log("[Auth] Profile loaded:", true);
            }
          }
        } else {
          // Secondary attempt for mobile browsers with slow storage access
          console.log("[Auth] Primary session check yielded nothing. Trying secondary bridge...");
          const { data: { user: refreshUser } } = await supabase.auth.getUser();
          if (refreshUser && mounted) {
            console.log("[Auth] Session recovered via secondary bridge!");
            setUser(refreshUser);
            const p = await fetchProfileById(refreshUser.id);
            if (p) applyProfile(p);
          } else {
            console.log("[Auth] Session recovered:", false);
          }
        }
      } catch (err) {
        console.error("[Auth] Initialization Error:", err);
      } finally {
        if (mounted) {
          clearTimeout(safetyTimeout);
          setLoading(false);
          loadingRef.current = false;
        }
      }

      if (!mounted) return;

      // ── Listen for Future Changes ─────────────────────────────────
      const syncProfile = async (currentUser: User) => {
        if (!mounted) return;
        // Perform profile sync/fetch
        let p = await fetchProfileById(currentUser.id, 1);

        // If no profile exists and it's a social login, create it (secondary net)
        if (!p && currentUser.app_metadata?.provider !== 'email') {
          console.log("[Auth] New social user detected during active session change. Syncing...");
          p = await upsertProfileFromSocialMetadata(currentUser);

          // Trigger Welcome Email Sequence
          if (p && currentUser.email) {
            const { sendAdminNotification } = await import("@/lib/notifications");
            try {
              await sendAdminNotification('student_welcome', {
                email: currentUser.email,
                name: p.full_name
              });
            } catch (notifyErr) {
              console.error("Welcome Mail error:", notifyErr);
            }
          }
        }

        if (mounted) {
          if (!p) {
             console.warn("[Auth] Active session has no profile. Escalating to deep cleanup...");
             signOut();
             return;
          }
          applyProfile(p);
        }
      };

      // NOTE: this callback must stay synchronous. supabase-js runs it while holding
      // the auth lock, so awaiting Supabase queries here stalls signInWithPassword /
      // getSession until they finish — the main cause of slow sign-ins.
      const { data: { subscription } } = supabase.auth.onAuthStateChange((event: string, currentSession: Session | null) => {
        if (!mounted) return;

        if (event === 'SIGNED_OUT' || event === 'USER_DELETED' || signingOut.current) {
           setUser(null);
           setProfile(null);
           setSession(null);
           if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
             // MISSION: Deep clean local storage to prevent refresh token loops
             attemptSessionCleanup("Explicit sign-out or deletion event.");
             window.location.replace('/login');
           }
           return;
        }

        // Handle token refresh failures (Critical for mobile)
        if (event === 'TOKEN_REFRESH_FAILED') {
          console.error("[Auth] Token refresh failed. Forcing re-authentication.");
          attemptSessionCleanup("Token refresh failed (mobile context).");
          return;
        }

        const currentUser = currentSession?.user ?? null;
        setSession(currentSession);
        setUser(currentUser);
        
        if (currentSession) {
          localStorage.setItem('ib_meridian_auth', 'true');
        } else {
          localStorage.removeItem('ib_meridian_auth');
        }

        if (currentUser && event !== 'INITIAL_SESSION') {
          // Token refreshes / tab-refocus SIGNED_IN events don't change the profile —
          // skip the extra round trip when it's already loaded for this user.
          if (event !== 'USER_UPDATED' && profileRef.current?.id === currentUser.id) return;
          setTimeout(() => { syncProfile(currentUser); }, 0);
        } else if (!currentUser) {
          setProfile(null);
          profileRef.current = null;
          writeCachedProfile(null);
        }
      });
      authSubscription = subscription;
    };

    setupAuth();

    return () => {
      mounted = false;
      clearTimeout(safetyTimeout);
      authSubscription?.unsubscribe();
    };
  }, []);

  return (
    <AuthContext.Provider value={{ user: (profile || user) as any, session, profile, loading, signOut, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
